import type { SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';

/**
 * Whether MPR / MIP can open, without loading vtk.js: the toolbar and context menu ask this on
 * every focus change, and vtk.js (~600 KB) is only fetched when a user actually opens MPR / MIP.
 */

export const MIN_SLICES = 3;
/** Modalities with a volumetric acquisition (spec section 2: "an MPR-capable modality"). */
export const MPR_MODALITIES = new Set(['CT', 'MR', 'PT', 'NM']);
/** Two orientations are "the same" when every cosine differs by less than this. */
export const ORIENTATION_TOLERANCE = 1e-3;

export function isMultiFrame(series: SeriesRef): boolean {
  return series.instances.some((instance) => instance.numberOfFrames > 1);
}

function activeSeries(shell: ViewerShell): SeriesRef | undefined {
  return shell.activeViewport()?.currentStack?.series;
}

interface PlaneCosines {
  rowCosines?: number[] | null;
  columnCosines?: number[] | null;
}

/** False only when two slices report different orientations (unknown metadata does not block). */
function uniformOrientation(imageIds: string[]): boolean {
  let reference: number[] | null = null;
  for (const imageId of imageIds) {
    const plane = window.cornerstone.metaData.get('imagePlaneModule', imageId) as PlaneCosines | undefined;
    if (!plane?.rowCosines || !plane.columnCosines) continue;
    const cosines = [...plane.rowCosines, ...plane.columnCosines].map(Number);
    if (!reference) {
      reference = cosines;
      continue;
    }
    const ref = reference;
    if (cosines.some((v, i) => Math.abs(v - ref[i]) > ORIENTATION_TOLERANCE)) return false;
  }
  return true;
}

/**
 * Whether the MPR / MIP entries apply to the active viewport: config `tools3d` on, and a series of at least
 * three single-frame CT / MR / PT / NM slices with one orientation.
 */
export function canOpenMpr(shell: ViewerShell): boolean {
  if (shell.config.tools3d === false) return false;
  const series = activeSeries(shell);
  if (!series || !MPR_MODALITIES.has(series.modality) || isMultiFrame(series)) return false;
  const imageIds = shell.imageIdsFor(series);
  return imageIds.length >= MIN_SLICES && uniformOrientation(imageIds);
}
